import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLiveCount } from "../hooks/useLiveCount";
import type { LiveCountStats } from "../scene/liveCount";
import css from "./LiveCountBanner.module.css";

/** ADR 363 — "contagem em andamento" on /inventory: the same number as the /live card,
 *  with the way to the floor. Renders nothing when no sweep is streaming. */
export function LiveCountBanner() {
  const { t, i18n } = useTranslation();
  const stats = useLiveCount();
  if (!stats) return null;
  return (
    <section className={css.banner} role="status" aria-label={t("liveCount.title")}>
      <span className={css.pulse} aria-hidden="true" />
      <div className={css.body}>
        <div className={css.title}>{t("liveCount.title")}</div>
        <div className={css.line}>{formatLiveCount(stats, t, i18n.language)}</div>
      </div>
      <Link className={css.link} to="/live">{t("liveCount.openLive")}</Link>
    </section>
  );
}

export type Tr = (key: string, opts?: Record<string, unknown>) => string;

/** "4.812 de 15.538 (31,0%) · 1.240/h · termina ~17:40" — shared by card and banner.
 *  `compact` drops the progress phrase for a surface whose value line already shows it. */
export function formatLiveCount(s: LiveCountStats, t: Tr, locale: string, compact = false): string {
  const parts: string[] = [];
  const n = (v: number) => v.toLocaleString(locale, { maximumFractionDigits: 0 });
  const pct = s.pct === null ? null
    : s.pct.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (compact) {
    if (pct !== null) parts.push(`${pct}%`);
  } else if (s.total !== null && pct !== null) {
    parts.push(t("liveCount.progress", { visited: n(s.visited), total: n(s.total), pct }));
  } else {
    parts.push(t("liveCount.visitedOnly", { visited: n(s.visited) }));
  }
  if (s.ratePerHour !== null) parts.push(t("liveCount.rate", { rate: n(s.ratePerHour) }));
  if (s.etaMs !== null) {
    parts.push(t("liveCount.eta", {
      time: new Date(s.etaMs).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
    }));
  }
  return parts.join(" · ");
}
